type TransformedData = {
  title: { [key: string]: string };
  description: { [key: string]: string };
};

type OriginalData = {
  descriptionItems: {
    title: { title: { language: { value: string; label: string }; value: string }[] };
    description: { description: { language: { value: string; label: string }; value: string }[] };
  }[];
};
type NotesData = {
  notes: {
    title: { title: { language: { value: string; label: string }; value: string }[] };
    description: { description: { language: { value: string; label: string }; value: string }[] };
  }[];
};

type Language = {
  name: string;
  code: string;
};

export function transformTitleDescriptionWithTranslations(data: any): { descriptionItems: TransformedData[] } {
  const transformedItems = data.descriptionItems.map((item: any) => {
    const transformed: TransformedData = {
      title: {},
      description: {},
    };

    item.title.title.forEach((titleItem: any) => {
      transformed.title[titleItem.language.value] = titleItem.value;
    });

    item.description.description.forEach((descriptionItem: any) => {
      transformed.description[descriptionItem.language.value] = descriptionItem.value;
    });

    return transformed;
  });

  return { descriptionItems: transformedItems };
}
export function transformNotes(data: any): { notes: TransformedData[] } {
  const transformedItems = data.notes.map((item: any) => {
    const transformed: TransformedData = {
      title: {},
      description: {},
    };

    item.title.title.forEach((titleItem: any) => {
      transformed.title[titleItem.language.value] = titleItem.value;
    });

    item.description.description.forEach((descriptionItem: any) => {
      transformed.description[descriptionItem.language.value] = descriptionItem.value;
    });

    return transformed;
  });

  return { notes: transformedItems };
}
export function transformDynamicTitleDescription(data: any, fieldname: any) {
  const transformed: TransformedData = {
    title: {},
    description: {},
  };
  data[fieldname].title.title.forEach((titleItem: any) => {
    transformed.title[titleItem.language.value] = titleItem.value;
  });

  data[fieldname].description.description.forEach((descriptionItem: any) => {
    transformed.description[descriptionItem.language.value] = descriptionItem.value;
  });

  return { [fieldname]: transformed };
}

export function transTitleDescriptionWithTranslationFromServer(serverData: any, languages: Language[]): OriginalData {
  const languageMap: { [key: string]: string } = {};
  languages.forEach((lang) => {
    languageMap[lang.code] = lang.name;
  });

  const transformedItems = serverData.descriptionItems.map((item: TransformedData) => {
    const originalTitle = Object.keys(item.title).map((lang: string) => ({
      language: { value: lang, label: languageMap[lang] || lang.charAt(0).toUpperCase() + lang.slice(1) },
      value: item.title[lang],
    }));

    const originalDescription = Object.keys(item.description).map((lang: string) => ({
      language: { value: lang, label: languageMap[lang] || lang.charAt(0).toUpperCase() + lang.slice(1) },
      value: item.description[lang],
    }));

    return {
      title: {
        title: originalTitle,
      },
      description: {
        description: originalDescription,
      },
    };
  });

  return {
    descriptionItems: transformedItems,
  };
}
export function transformDynamicTitleDescriptionFrom(serverData: any, languages: Language[], fieldname: string): any {
  const languageMap: { [key: string]: string } = {};
  languages.forEach((lang) => {
    languageMap[lang.code] = lang.name;
  });

  // Extract the transformed data
  const transformedData = serverData[fieldname];

  // Reconstruct the original structure
  const originalTitle = Object.keys(transformedData.title).map((lang: string) => ({
    language: { value: lang, label: languageMap[lang] || lang.charAt(0).toUpperCase() + lang.slice(1) },
    value: transformedData.title[lang],
  }));

  const originalDescription = Object.keys(transformedData.description).map((lang: string) => ({
    language: { value: lang, label: languageMap[lang] || lang.charAt(0).toUpperCase() + lang.slice(1) },
    value: transformedData.description[lang],
  }));

  // Return the original structure
  return {
    [fieldname]: {
      title: {
        title: originalTitle,
      },
      description: {
        description: originalDescription,
      },
    },
  };
}
export function transNotesFromServer(serverData: any, languages: Language[]): NotesData {
  const languageMap: { [key: string]: string } = {};
  languages.forEach((lang) => {
    languageMap[lang.code] = lang.name;
  });

  const transformedItems = serverData.notes.map((item: TransformedData) => {
    const originalTitle = Object.keys(item.title).map((lang: string) => ({
      language: { value: lang, label: languageMap[lang] || lang.charAt(0).toUpperCase() + lang.slice(1) },
      value: item.title[lang],
    }));

    const originalDescription = Object.keys(item.description).map((lang: string) => ({
      language: { value: lang, label: languageMap[lang] || lang.charAt(0).toUpperCase() + lang.slice(1) },
      value: item.description[lang],
    }));

    return {
      title: {
        title: originalTitle,
      },
      description: {
        description: originalDescription,
      },
    };
  });

  return {
    notes: transformedItems,
  };
}

export function transformContent(content: any) {
  const transformed = content.map((item: any) => {
    // Transform title
    const title = item.title.title.reduce((acc: any, titleItem: any) => {
      acc[titleItem.language.value] = titleItem.value;
      return acc;
    }, {});

    // Transform details
    const details = item.details.map((detail: any) => {
      return {
        icon: detail.icon.path,
        description: detail.description.description.reduce((acc: any, descItem: any) => {
          acc[descItem.language.value] = descItem.value;
          return acc;
        }, {}),
      };
    });

    return {
      title: title,
      details: details,
    };
  });
  return { content: transformed };
}

export function transformServerContentBackToOriginal(transformedData: any, languages: any) {
  // Create a language map for quick lookup
  const languageMap = languages.reduce((acc: any, lang: any) => {
    acc[lang.code] = lang.name;
    return acc;
  }, {});

  return transformedData.content.map((item: any) => {
    // Transform title back to original structure
    const title = {
      title: Object.entries(item.title).map(([key, value]) => ({
        language: { value: key, label: languageMap[key] },
        value: value,
      })),
    };

    // Transform details back to original structure
    const details = item.details.map((detail: any) => {
      return {
        icon: {
          path: detail.icon, // Use the icon path from the transformed data
        },
        description: {
          description: Object.entries(detail.description).map(([key, value]) => ({
            language: { value: key, label: languageMap[key] },
            value: value,
          })),
        },
      };
    });

    return {
      title: title,
      details: details,
    };
  });
}

// this ones are cleaned and managed:
export function transformFieldWithTranslationForServer(data: any, name: string): any {
  const transformed: any = {
    [name]: {},
  };

  data.forEach((item: any) => {
    transformed[name][item.language.value] = item.value;
  });

  return transformed;
}

export const transformFieldWithTranslationFromServer = (serverData: any, languages: Array<{ name: string; code: string }>, fieldName: string): any => {
  const languageMap = languages.reduce<Record<string, string>>((acc, lang) => {
    acc[lang.code] = lang.name;
    return acc;
  }, {});

  const transformed = Object.entries(serverData).map(([key, value]) => ({
    language: { value: key, label: languageMap[key] },
    value: value,
  }));

  return {
    [fieldName]: transformed,
  };
};

interface TranslationItem {
  language: {
    value: string;
    label: string;
  };
  value: string;
}

interface OriginalFormat {
  title: TranslationItem[];
  description: TranslationItem[];
}

interface NestedFormat {
  details: any;
}

interface OriginalFormatItem {
  title: Array<{
    language: { value: string; label: string };
    value: string;
  }>;
  description: Array<{
    language: { value: string; label: string };
    value: string;
  }>;
}

export function transformFaqForServer(
  originalData: Array<{
    title: Array<{ language: { value: string; label: string }; value: string }>;
    description: Array<{ language: { value: string; label: string }; value: string }>;
  }>
): { details: Array<{ title: Record<string, string>; description: Record<string, string> }> } {
  return {
    details: originalData.map((item) => ({
      title: transformFieldWithTranslationForServer(item.title, "title").title,
      description: transformFieldWithTranslationForServer(item.description, "description").description,
    })),
  };
}

export function transformFaqFromServer(nestedData: NestedFormat, languages: Language[]): OriginalFormatItem[] {
  return nestedData.details.map((detail: any) => ({
    title: transformFieldWithTranslationFromServer(detail.title, languages, "title").title,
    description: transformFieldWithTranslationFromServer(detail.description, languages, "description").description,
  }));
}
